import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';


import { useDebounce } from 'hooks';
import { FilterMatchMode } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import {
  TriStateCheckbox,
  TriStateCheckboxChangeEvent
} from 'primereact/tristatecheckbox';
import { classNames } from 'primereact/utils';
import { selectedDate } from 'types/interfaces';
import { clearFilter } from 'utils';

import { DepartmentsService } from 'components/services/DepartmentsService';
import { CustomDropdown } from 'ui/custom-controls';
import Datepicker from 'ui/custom-controls/Datepicker';
import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';

import { departmentAssignmentHistoryColumnWidths as colW } from '../config/columnWidths';
import { getAssignmentHistoryErrorFilterOptions } from '../config/getAssignmentHistoryErrorFilterOptions';
import {
  type DepartmentAssignmentHistoryFilterOption,
  departmentAssignmentHistoryTypeFilterOptions
} from '../config/typeFilterOptions';
import {
  DEPARTMENT_ASSIGNMENT_HISTORY_ERROR_LABELS,
  formatDepartmentAssignmentErrors,
  formatDepartmentAssignmentHistoryType
} from '../model/departmentAssignmentHistory';
import type { DepartmentAssignmentHistoryItem } from '../model/types';

/**
 * TODO(refactor-history-tables): почти дублирует PersonAssignmentHistory — вынести в
 * useAssignmentHistoryTable(options) или общий layout + конфиг колонок.
 * Общее: loadData, JSON.stringify фильтров, dateBetween, debounce, onFiltersChange, scrollHeight,
 * пагинация, isValidation, enum type/errors + CustomDropdown, formatDepartmentAssignment*, clearFilter.
 * Различия: поля строки, сервис API, ключ localStorage дат, набор колонок. См. PersonAssignmentHistory.tsx.
 */

export default function DepartmentAssignmentHistory() {
  const [assignments, setAssignments] = useState<
    DepartmentAssignmentHistoryItem[]
  >([]);
  const [pageSize, setPageSize] = useState(100);
  const isFirstRender = useRef(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDateFilter, setSelectedDateFilter] = useState<selectedDate>(
    () => {
      const saved = localStorage.getItem('selectedAssignmentHistoryDateFilter');
      return saved
        ? JSON.parse(saved)
        : {
          dateFrom: '',
          dateUntil: ''
        };
    }
  );
  const toast = useRef<Toast>(null);
  const [validationFilter, setValidationFilter] = useState<boolean | null>(
    null
  );

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    asset: { value: null, matchMode: FilterMatchMode.CONTAINS },
    date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    user: { value: null, matchMode: FilterMatchMode.CONTAINS },
    newDepartment: { value: null, matchMode: FilterMatchMode.CONTAINS },
    previousDepartment: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    errors: { value: null, matchMode: FilterMatchMode.EQUALS }
  });

  const idFilterRef = useRef<HTMLInputElement>(null);
  const taskTypeFilterRef = useRef<HTMLInputElement>(null);
  const assetFilterRef = useRef<HTMLInputElement>(null);
  const timeFilterRef = useRef<HTMLInputElement>(null);
  const authorFilterRef = useRef<HTMLInputElement>(null);
  const departmentFilterRef = useRef<HTMLInputElement>(null);
  const previousDepartmentFilterRef = useRef<HTMLInputElement>(null);

  const tableElement = document.querySelector('.p-datatable-thead');

  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setPageSize(event.rows);
      setFirst(0);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  function offset(el: Element) {
    const rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  const getValidTimestamp = (value: string | undefined) => {
    if (!value) return '';

    let num: number;

    if (
      typeof value === 'string' &&
      (value.includes('T') || value.includes('Z'))
    ) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString();
    }

    num = parseInt(value, 10);
    if (isNaN(num) || num < 1577836800000) {
      return '';
    }
    return new Date(num).toISOString();
  };

  const loadData = useCallback(() => {
    const sort = multiSortMeta?.[0];
    const mergedFilters: Record<string, DataTableFilterMetaData> = {
      ...filters,
      isValidation: {
        value: validationFilter,
        matchMode: FilterMatchMode.EQUALS
      }
    };
    const baseFilters: {
      field: string;
      value: unknown;
      operator?: string;
      additionalValue?: string;
    }[] = Object.keys(mergedFilters).map((key) => {
      const f = mergedFilters[key];
      return {
        field: key,
        value: f?.value,
        operator: f?.matchMode
      };
    });
    baseFilters.push({
      field: 'dateBetween',
      value: getValidTimestamp(selectedDateFilter.dateFrom),
      additionalValue: getValidTimestamp(selectedDateFilter.dateUntil),
      operator: FilterMatchMode.BETWEEN
    });
    const payload = JSON.stringify(baseFilters);

    const service = DepartmentsService.getDepartmentAssignmentHistory({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: payload
    });

    service.then((res) => {
      if (res.data.isSuccess) {
        const list = (res.data.value.itemsList ||
          []) as DepartmentAssignmentHistoryItem[];
        setAssignments(list);
        setTotalCount(res.data.value.total || 0);
      }
    });
  }, [filters, multiSortMeta, pageNumber, pageSize, selectedDateFilter, validationFilter]);

  const { debouncedCallback: debouncedLoadData } = useDebounce(loadData, 300);

  useEffect(() => {
    debouncedLoadData();
  }, [filters, multiSortMeta, pageNumber, pageSize, selectedDateFilter, validationFilter]);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta ?? null);
  }, []);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    const { isValidation: _omitValidation, ...nextWithoutValidation } = next;
    setFilters((prev) => ({
      ...prev,
      ...nextWithoutValidation,
      global: next.global ?? prev.global,
      type: next.type ?? prev.type,
      errors: next.errors ?? prev.errors
    }));
  }, []);

  const bumpPageToFirst = useCallback(() => {
    setPageNumber(1);
    setFirst(0);
  }, []);

  const onFilterChange = useCallback(
    (filter: string, e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFilters((prev) => {
        const _filters = JSON.parse(JSON.stringify(prev));
        if (!_filters[filter]) return prev;
        _filters[filter].value = value || null;
        return _filters;
      });
      bumpPageToFirst();
    },
    [bumpPageToFirst]
  );

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const taskIdBodyTemplate = (rowData: DepartmentAssignmentHistoryItem) => {
    return (
      <span
        style={{ cursor: 'pointer', color: '#007ad9' }}
        onClick={() => {
          window.open(`/${rowData.taskId}/?tab=history`, '_blank');
        }}
      >
        {rowData.taskId}
      </span>
    );
  };

  const dateBodyTemplate = (rowData: DepartmentAssignmentHistoryItem) => (
    <span style={{ whiteSpace: 'nowrap' }}>
      {rowData.dateFormatted ?? rowData.date ?? ''}
    </span>
  );

  const typeBodyTemplate = (rowData: DepartmentAssignmentHistoryItem) =>
    formatDepartmentAssignmentHistoryType(rowData.type);

  const errorsBodyTemplate = (rowData: DepartmentAssignmentHistoryItem) =>
    formatDepartmentAssignmentErrors(rowData.errors, DEPARTMENT_ASSIGNMENT_HISTORY_ERROR_LABELS);

  const validationBodyTemplate = (rowData: DepartmentAssignmentHistoryItem) => (
    <i
      className={classNames('pi', {
        'true-icon pi-check-circle': rowData.isValidation,
        'false-icon pi-times-circle': !rowData.isValidation
      })}
    />
  );

  const selectedTypeFilterOption = useMemo<DepartmentAssignmentHistoryFilterOption | null>(
    () => {
      const currentValue = filters.type?.value;
      if (currentValue === null || currentValue === undefined) {
        return null;
      }
      return (
        departmentAssignmentHistoryTypeFilterOptions.find(
          (option) => option.value === currentValue
        ) ?? null
      );
    },
    [filters.type?.value]
  );

  const selectedErrorsFilterOption = useMemo<DepartmentAssignmentHistoryFilterOption | null>(
    () => {
      const currentValue = filters.errors?.value;
      if (currentValue === null || currentValue === undefined) {
        return null;
      }
      return (
        getAssignmentHistoryErrorFilterOptions(DEPARTMENT_ASSIGNMENT_HISTORY_ERROR_LABELS).find(
          (option) => option.value === currentValue
        ) ?? null
      );
    },
    [filters.errors?.value]
  );

  useEffect(() => {
    localStorage.setItem(
      'selectedAssignmentHistoryDateFilter',
      JSON.stringify(selectedDateFilter)
    );
  }, [selectedDateFilter]);

  const handleDateFilterChange = useCallback((selection: selectedDate) => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const isDateFromValid =
      selection.dateFrom !== null &&
      !isNaN(new Date(selection.dateFrom)?.getTime());
    const isDateUntilValid =
      selection.dateUntil !== null &&
      !isNaN(new Date(selection.dateUntil)?.getTime());
    if (isDateFromValid || isDateUntilValid) {
      const newDateFrom = isDateFromValid
        ? String(new Date(selection.dateFrom!)?.getTime())
        : '';
      const newDateUntil = isDateUntilValid
        ? String(new Date(selection.dateUntil!)?.getTime())
        : '';

      if (
        newDateFrom !== selectedDateFilter.dateFrom ||
        newDateUntil !== selectedDateFilter.dateUntil
      ) {
        setSelectedDateFilter({
          dateFrom: newDateFrom,
          dateUntil: newDateUntil
        });
        setPageNumber(1);
        setFirst(0);
      }
    } else {
      setSelectedDateFilter({ dateFrom: '', dateUntil: '' });
      setPageNumber(1);
      setFirst(0);
    }
  }, []);

  const renderHeader = () => {
    return (
      <div id="table-header" className="flex gap-2">
        <span
          id="departmentAssignmentHistoryGlobalSearchHint"
          style={{ display: 'inline-block' }}
        >
          <SearchInputWithClear
            onChange={(value) => {
              const mockEvent = {
                target: { value }
              } as React.ChangeEvent<HTMLInputElement>;
              debouncedOnFilterChange('global', mockEvent);
            }}
            placeholder="Поиск по ключевым словам"
          />
          <Tooltip
            target="#departmentAssignmentHistoryGlobalSearchHint"
            content='Глобальный поиск не работает по полям "Статус распределения" и "Примечание".'
            position="right"
          />
        </span>
        <Datepicker
          datepickerDate={selectedDateFilter as selectedDate}
          onChange={handleDateFilterChange}
        />
      </div>
    );
  };

  const header = renderHeader();

  return (
    <div className="departmentAssignmentHistory-table wrapper wrapper--main">
      <Toast ref={toast} />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        metaKeySelection={false}
        scrollable
        paginator
        first={first}
        rows={pageSize}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPage={onPageChanged}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${assignments.length > 0 ? totalCount : 0}`}
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={assignments}
        dataKey="id"
        filterDisplay="row"
        header={header}
        emptyMessage="Распределений не найдено."
        onRowReorder={(e) => setAssignments(e.value)}
        tableStyle={{ minWidth: '100%', width: '100%' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="taskId"
          filter
          sortable
          field="taskId"
          header="№ наряда"
          style={{ width: colW.taskId }}
          headerStyle={{ width: colW.taskId }}
          onFilterClear={() =>
            clearFilter('taskId', filters, setFilters, idFilterRef, setPageNumber, setFirst)
          }
          body={taskIdBodyTemplate}
          filterElement={
            <InputText
              ref={idFilterRef}
              className="w-full"
              onChange={handleInputChange('taskId')}
              placeholder="Поиск по № наряда"
            />
          }
        />
        <Column
          columnKey="type"
          filter
          sortable
          field="type"
          header={
            <span className="flex align-items-center gap-2">
              <span>Статус распределения</span>
              <i
                className="pi pi-info-circle department-assignment-status-filter-hint"
                style={{ color: '#3b82f6', cursor: 'pointer' }}
                data-pr-tooltip='Глобальный поиск не работает по полю "Статус распределения".'
              />
            </span>
          }
          style={{ width: colW.type }}
          headerStyle={{ width: colW.type }}
          body={typeBodyTemplate}
          onFilterClear={() => {
            setFilters((prev) => ({
              ...prev,
              type: { ...prev.type, value: null, matchMode: FilterMatchMode.EQUALS }
            }));
            setPageNumber(1);
            setFirst(0);
          }}
          filterElement={
            <div className="w-full">
              <CustomDropdown<DepartmentAssignmentHistoryFilterOption>
                style={{ maxWidth: '125px', minWidth: 0 }}
                assetOptions={departmentAssignmentHistoryTypeFilterOptions}
                selectedAsset={selectedTypeFilterOption}
                fieldName="label"
                placeholder="Все"
                showClearOnHover
                onSelectionChange={(selected) => {
                  const selectedOption =
                    (selected as DepartmentAssignmentHistoryFilterOption | null) ?? null;
                  setFilters((prev) => ({
                    ...prev,
                    type: {
                      ...prev.type,
                      value: selectedOption?.value ?? null,
                      matchMode: FilterMatchMode.EQUALS
                    }
                  }));
                  bumpPageToFirst();
                }}
              />
            </div>
          }
        />
        <Column
          columnKey="taskType"
          filter
          sortable
          field="taskType"
          header="Тип наряда"
          style={{ width: colW.taskType }}
          headerStyle={{ width: colW.taskType }}
          onFilterClear={() =>
            clearFilter(
              'taskType',
              filters,
              setFilters,
              taskTypeFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={taskTypeFilterRef}
              className="w-full"
              onChange={handleInputChange('taskType')}
              placeholder="Поиск по типу"
            />
          }
        />
        <Column
          columnKey="asset"
          filter
          sortable
          field="asset"
          header="Объект"
          style={{ width: colW.asset }}
          headerStyle={{ width: colW.asset }}
          onFilterClear={() =>
            clearFilter('asset', filters, setFilters, assetFilterRef, setPageNumber, setFirst)
          }
          filterElement={
            <InputText
              ref={assetFilterRef}
              className="w-full"
              onChange={handleInputChange('asset')}
              placeholder="Поиск по объекту"
            />
          }
        />
        <Column
          columnKey="date"
          filter
          sortable
          field="date"
          filterField="date"
          sortField="date"
          header="Дата и время распределения"
          style={{ width: colW.date }}
          headerStyle={{
            width: colW.date,
            whiteSpace: 'normal',
            textAlign: 'center',
            lineHeight: 1.25
          }}
          body={dateBodyTemplate}
          onFilterClear={() =>
            clearFilter('date', filters, setFilters, timeFilterRef, setPageNumber, setFirst)
          }
          filterElement={
            <InputText
              ref={timeFilterRef}
              className="w-full"
              onChange={handleInputChange('date')}
              placeholder="Поиск по дате"
            />
          }
        />
        <Column
          columnKey="user"
          filter
          sortable
          field="user"
          header="Кем назначен"
          style={{ width: colW.user }}
          headerStyle={{ width: colW.user }}
          onFilterClear={() =>
            clearFilter('user', filters, setFilters, authorFilterRef, setPageNumber, setFirst)
          }
          filterElement={
            <InputText
              ref={authorFilterRef}
              className="w-full"
              onChange={handleInputChange('user')}
              placeholder="Поиск по пользователю"
            />
          }
        />
        <Column
          columnKey="newDepartment"
          filter
          sortable
          field="newDepartment"
          header="Участок"
          style={{ width: colW.newDepartment }}
          headerStyle={{ width: colW.newDepartment }}
          onFilterClear={() =>
            clearFilter(
              'newDepartment',
              filters,
              setFilters,
              departmentFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={departmentFilterRef}
              className="w-full"
              onChange={handleInputChange('newDepartment')}
              placeholder="Поиск по участку"
            />
          }
        />
        <Column
          columnKey="previousDepartment"
          filter
          sortable
          field="previousDepartment"
          header="Прошлый участок"
          style={{ width: colW.previousDepartment }}
          headerStyle={{ width: colW.previousDepartment }}
          onFilterClear={() =>
            clearFilter(
              'previousDepartment',
              filters,
              setFilters,
              previousDepartmentFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={previousDepartmentFilterRef}
              className="w-full"
              onChange={handleInputChange('previousDepartment')}
              placeholder="Поиск по участку"
            />
          }
        />
        <Column
          columnKey="isValidation"
          filter
          sortable
          field="isValidation"
          header="Валидация"
          dataType="boolean"
          style={{ width: colW.isValidation }}
          headerStyle={{ width: colW.isValidation }}
          body={validationBodyTemplate}
          onFilterClear={() => {
            setValidationFilter(null);
            setPageNumber(1);
            setFirst(0);
          }}
          filterElement={
            <TriStateCheckbox
              id="assignmentHistoryValidationFilter"
              value={validationFilter}
              onChange={(e: TriStateCheckboxChangeEvent) => {
                setValidationFilter(e.value ?? null);
                bumpPageToFirst();
              }}
            />
          }
        />
        <Column
          columnKey="errors"
          filter
          sortable
          field="errors"
          header={
            <span className="flex align-items-center gap-2">
              <span>Примечание</span>
              <i
                className="pi pi-info-circle department-assignment-errors-filter-hint"
                style={{ color: '#3b82f6', cursor: 'pointer' }}
                data-pr-tooltip='Глобальный поиск не работает по полю "Примечание".'
              />
            </span>
          }
          style={{ width: colW.errors }}
          headerStyle={{ width: colW.errors }}
          body={errorsBodyTemplate}
          onFilterClear={() => {
            setFilters((prev) => ({
              ...prev,
              errors: { ...prev.errors, value: null, matchMode: FilterMatchMode.EQUALS }
            }));
            bumpPageToFirst();
          }}
          filterElement={
            <div className="w-full">
              <CustomDropdown<DepartmentAssignmentHistoryFilterOption>
                assetOptions={getAssignmentHistoryErrorFilterOptions(DEPARTMENT_ASSIGNMENT_HISTORY_ERROR_LABELS)}
                selectedAsset={selectedErrorsFilterOption}
                style={{ maxWidth: '192px', minWidth: 0 }}
                fieldName="label"
                placeholder="Все"
                showClearOnHover
                onSelectionChange={(selected) => {
                  const selectedOption =
                    (selected as DepartmentAssignmentHistoryFilterOption | null) ?? null;
                  setFilters((prev) => ({
                    ...prev,
                    errors: {
                      ...prev.errors,
                      value: selectedOption?.value ?? null,
                      matchMode: FilterMatchMode.EQUALS
                    }
                  }));
                  bumpPageToFirst();
                }}
              />
            </div>
          }
        />
      </DataTable>
      <Tooltip target=".department-assignment-status-filter-hint" position="top" />
      <Tooltip target=".department-assignment-errors-filter-hint" position="top" />
    </div>
  );
}
